import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Column,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  FormField,
  Heading,
  MultiSelect,
  Radio,
  RadioGroup,
  Row,
  Select,
  Spinner,
  Text,
  TextInput,
} from "@hightouch/htui";
import { CAMPAIGN_GROUPS } from "@hightouch/lib/customer-data/decision-engine/send-insights/types";
import { useSearchParams } from "src/router";
import { useComputeSendInsightChartsQuery } from "src/graphql";

import type {
  CampaignGroup,
  ChartConfig,
  ChartResult,
  GridChartResult,
  LabelResolver,
  LineChartResult,
  SendInsightDimension,
  SendInsightTab,
} from "./types";
import type { ChartType } from "./types";
import { DIMENSION_LABELS, getMetricPresets } from "./types";
import { SendInsightsLineChart } from "./line-chart";
import { SendInsightsGridChart } from "./grid-chart";
import {
  type BuilderState,
  getDefaultBuilderState,
  parseBuilderParams,
} from "./chart-builder-utils";

const CAMPAIGN_GROUP_OPTIONS = [
  { value: CAMPAIGN_GROUPS.THIS_FLOW, label: "This flow" },
  { value: CAMPAIGN_GROUPS.CUSTOMER_MANAGED, label: "Customer managed" },
  { value: CAMPAIGN_GROUPS.OTHER_FLOWS, label: "Other flows" },
  {
    value: CAMPAIGN_GROUPS.OTHER_FLOWS_CUSTOMER_MANAGED,
    label: "Other flows (customer managed)",
  },
  { value: CAMPAIGN_GROUPS.UNACCOUNTED, label: "Unaccounted" },
  { value: CAMPAIGN_GROUPS.NULL_CAMPAIGN, label: "No campaign" },
];

const FIDELITY_EXPERIMENT_FILTER = ["treatment", "uniform", "mab"];

const COMPARISON_SEGMENT2_ALLOWED: SendInsightDimension[] = [
  "none",
  "channel_type",
  "campaign_group",
  "day_of_week",
  "time_bucket",
];

const FIDELITY_SEGMENT_ALLOWED: SendInsightDimension[] = [
  "none",
  "channel_type",
  "channel",
  "message_id",
  "campaign_group",
  "match_status",
  "day_of_week",
  "time_bucket",
];

function getDimensionOptions(
  allowed: SendInsightDimension[],
): { value: SendInsightDimension; label: string }[] {
  return allowed.map((dim) => ({ value: dim, label: DIMENSION_LABELS[dim] }));
}

const COMPARISON_SEGMENT_OPTIONS = getDimensionOptions(
  COMPARISON_SEGMENT2_ALLOWED,
);
const FIDELITY_SEGMENT_OPTIONS = getDimensionOptions(FIDELITY_SEGMENT_ALLOWED);
const SEGMENT2_OPTIONS = getDimensionOptions(COMPARISON_SEGMENT2_ALLOWED);

interface ChartBuilderProps {
  onSave: (chart: ChartConfig) => Promise<void>;
  isSaving: boolean;
  metadataColumns: string[];
  tab: SendInsightTab;
  channelTypes: string[];
  flowId: string;
  resolveLabel: LabelResolver;
}

export function ChartBuilder({
  onSave,
  isSaving,
  metadataColumns,
  tab,
  channelTypes,
  flowId,
  resolveLabel,
}: ChartBuilderProps) {
  const [search, setSearch] = useSearchParams();
  const builderState = parseBuilderParams(search);
  const isOpen = builderState !== null;

  const isComparison = tab === "comparison";
  const [state, setState] = useState<BuilderState>(getDefaultBuilderState(tab));
  const [previewConfig, setPreviewConfig] = useState<ChartConfig | null>(null);

  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setState(builderState ?? getDefaultBuilderState(tab));
      setPreviewConfig(null);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, builderState, tab]);

  const channelTypeOptions = useMemo(
    () => channelTypes.map((ct) => ({ value: ct, label: ct })),
    [channelTypes],
  );

  const presets = useMemo(
    () => getMetricPresets(metadataColumns, tab),
    [metadataColumns, tab],
  );
  const presetOptions = useMemo(
    () =>
      Object.entries(presets)
        .filter(
          ([, preset]) =>
            state.chartType !== "grid" ||
            preset.denominator !== "rolling_30d_users",
        )
        .map(([key, preset]) => ({
          value: key,
          label: preset.label,
        })),
    [presets, state.chartType],
  );

  const segmentByOptions = isComparison
    ? COMPARISON_SEGMENT_OPTIONS
    : FIDELITY_SEGMENT_OPTIONS;

  const updateState = useCallback((patch: Partial<BuilderState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleClose = useCallback(() => {
    setSearch({}, { replace: true });
  }, [setSearch]);

  const buildChartConfig = useCallback((): ChartConfig => {
    const preset = presets[state.presetKey] ?? Object.values(presets)[0]!;

    const effectiveSegmentBy: SendInsightDimension = isComparison
      ? "experiment"
      : state.segmentBy;

    const filters: ChartConfig["filters"] = {};
    if (state.filterChannelTypes.length > 0) {
      filters.channel_types = state.filterChannelTypes;
    }
    if (state.filterCampaignGroups.length > 0) {
      filters.campaign_groups = state.filterCampaignGroups;
    }
    if (!isComparison) {
      filters.experiments = FIDELITY_EXPERIMENT_FILTER;
    }

    return {
      id: `preview-${Date.now()}`,
      name: state.name.trim() || "Untitled chart",
      type: state.chartType,
      numerator: preset.numerator,
      denominator: preset.denominator,
      segment_by: effectiveSegmentBy,
      ...(state.segmentBy2 !== "none" && { segment_by_2: state.segmentBy2 }),
      filters,
      source: "user",
      ...(state.chartType === "grid" && { window_days: state.windowDays }),
      ...(preset.rolling_window_days && {
        rolling_window_days: preset.rolling_window_days,
      }),
    };
  }, [state, presets, isComparison]);

  const handleSave = async () => {
    const chart = buildChartConfig();
    chart.id = `user-${Date.now()}`;
    await onSave(chart);
    handleClose();
  };

  const previewQuery = useComputeSendInsightChartsQuery(
    { flowId, charts: previewConfig ? [previewConfig] : [] },
    {
      enabled: previewConfig !== null,
      select: (data) => {
        const result = data.computeSendInsightCharts as {
          charts: ChartResult[];
        };
        return result.charts[0] ?? null;
      },
    },
  );

  const handlePreview = () => {
    setPreviewConfig(buildChartConfig());
  };

  const isValid = state.name.trim().length > 0;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="xl">
      <DrawerHeader>
        <Heading>Add chart</Heading>
      </DrawerHeader>
      <DrawerBody>
        <Row gap={6} align="start">
          <Column gap={5} flex="0 0 320px">
            <FormField label="Chart name">
              <TextInput
                placeholder="e.g. Match rate by channel"
                value={state.name}
                onChange={(e) => updateState({ name: e.target.value })}
              />
            </FormField>

            <FormField label="Chart type">
              <RadioGroup
                value={state.chartType}
                onChange={(val) => {
                  const next = val as ChartType;
                  const patch: Partial<BuilderState> = { chartType: next };
                  if (
                    next === "grid" &&
                    presets[state.presetKey]?.denominator ===
                      "rolling_30d_users"
                  ) {
                    const firstValid = Object.entries(presets).find(
                      ([, p]) => p.denominator !== "rolling_30d_users",
                    );
                    patch.presetKey = firstValid?.[0] ?? "send_count";
                  }
                  updateState(patch);
                }}
              >
                <Radio value="line" label="Line chart (over time)" />
                <Radio value="grid" label="Grid chart (heatmap)" />
              </RadioGroup>
            </FormField>

            <FormField label="Metric">
              <Select
                options={presetOptions}
                value={
                  presetOptions.some((o) => o.value === state.presetKey)
                    ? state.presetKey
                    : presetOptions[0]?.value
                }
                onChange={(val) => {
                  if (val) updateState({ presetKey: val });
                }}
              />
            </FormField>

            {!isComparison && (
              <FormField label="Segment by">
                <Select
                  options={segmentByOptions}
                  value={state.segmentBy}
                  onChange={(val) => {
                    if (val)
                      updateState({
                        segmentBy: val as SendInsightDimension,
                      });
                  }}
                />
              </FormField>
            )}

            <FormField
              label={
                isComparison
                  ? "Segment by (optional)"
                  : "Second segment (optional)"
              }
            >
              <Select
                options={SEGMENT2_OPTIONS}
                value={state.segmentBy2}
                onChange={(val) => {
                  if (val)
                    updateState({ segmentBy2: val as SendInsightDimension });
                }}
              />
            </FormField>

            {state.chartType === "grid" && (
              <FormField label="Window (days)">
                <TextInput
                  type="number"
                  value={state.windowDays === 0 ? "" : String(state.windowDays)}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    updateState({
                      windowDays: Number.isNaN(parsed) ? 0 : parsed,
                    });
                  }}
                  onBlur={() => {
                    if (state.windowDays < 1) {
                      updateState({ windowDays: 7 });
                    }
                  }}
                />
              </FormField>
            )}

            <FormField label="Filter by channel type (optional)">
              <MultiSelect
                fontWeight="medium"
                placeholder="All channel types"
                options={channelTypeOptions}
                value={state.filterChannelTypes}
                onChange={(val) => updateState({ filterChannelTypes: val })}
              />
            </FormField>

            <FormField label="Filter by campaign group (optional)">
              <MultiSelect
                fontWeight="medium"
                placeholder="All campaign groups"
                options={CAMPAIGN_GROUP_OPTIONS}
                value={state.filterCampaignGroups}
                onChange={(val) =>
                  updateState({
                    filterCampaignGroups: val as CampaignGroup[],
                  })
                }
              />
            </FormField>

            <Button variant="secondary" onClick={handlePreview}>
              Preview
            </Button>
          </Column>

          <Column flex={1} minWidth={0}>
            <PreviewPanel
              config={previewConfig}
              result={previewQuery.data ?? null}
              isLoading={previewQuery.isLoading}
              error={previewQuery.error}
              resolveLabel={resolveLabel}
            />
          </Column>
        </Row>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!isValid}
          >
            Save chart
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </Drawer>
  );
}

function PreviewPanel({
  config,
  result,
  isLoading,
  error,
  resolveLabel,
}: {
  config: ChartConfig | null;
  result: ChartResult | null;
  isLoading: boolean;
  error: unknown;
  resolveLabel: LabelResolver;
}) {
  if (!config) {
    return (
      <Column
        align="center"
        justify="center"
        py={12}
        borderWidth="1px"
        borderColor="base.border"
        borderRadius="md"
        borderStyle="dashed"
      >
        <Text color="text.tertiary">
          Configure your chart and click Preview to see the result.
        </Text>
      </Column>
    );
  }

  if (isLoading) {
    return (
      <Column align="center" justify="center" py={12}>
        <Spinner size="lg" />
        <Text color="text.secondary" mt={3}>
          Computing preview...
        </Text>
      </Column>
    );
  }

  if (error) {
    return (
      <Column
        align="center"
        justify="center"
        py={12}
        borderWidth="1px"
        borderColor="danger.border"
        borderRadius="md"
        bg="danger.background"
        px={4}
      >
        <Text color="danger.base">
          Preview failed:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </Text>
      </Column>
    );
  }

  if (!result) {
    return (
      <Column align="center" justify="center" py={12}>
        <Text color="text.secondary">
          No data available for this configuration.
        </Text>
      </Column>
    );
  }

  return (
    <Column
      borderWidth="1px"
      borderColor="base.border"
      borderRadius="md"
      overflow="hidden"
    >
      <Row
        justify="space-between"
        align="center"
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor="base.border"
        bg="gray.50"
      >
        <Text fontWeight="medium" size="md">
          {config.name || "Preview"}
        </Text>
        {config.type === "grid" && config.window_days && (
          <Text size="sm" color="text.secondary">
            Last {config.window_days} days
          </Text>
        )}
      </Row>
      <Box p={4}>
        {result.type === "line" ? (
          <SendInsightsLineChart
            config={config}
            result={result as LineChartResult}
            resolveLabel={resolveLabel}
          />
        ) : (
          <SendInsightsGridChart
            config={config}
            result={result as GridChartResult}
            resolveLabel={resolveLabel}
          />
        )}
      </Box>
    </Column>
  );
}
